import { useState } from "react";
import axios from "axios";
import { API, apiError } from "@/lib/api";
import { MapPin, Clock, Phone, CalendarPlus, LockSimple, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Title } from "@/components/demo/DemoSections";

const field = "block w-full min-w-0 max-w-full appearance-none rounded-[calc(var(--r)*.5)] border border-[var(--line)] bg-[var(--bg)] px-4 py-3.5 text-[15px] text-[var(--ink)] outline-none disabled:cursor-not-allowed [&::-webkit-date-and-time-value]:text-left";
export const DZ_FIELD = field;
const EMPTY = { date: "", time: "20:00", people: 2, name: "", phone: "", special_request: "", website: "" };
const today = () => new Date().toISOString().slice(0, 10);

function People({ value, onChange, st, disabled }) {
  return (
    <div className="grid grid-cols-6 gap-2">{[1, 2, 3, 4, 5, 6].map((n) => (
      <button type="button" key={n} disabled={disabled} onClick={() => onChange(n)} data-testid={`res-people-${n}`}
        className="flex h-11 items-center justify-center rounded-[calc(var(--r)*.5)] border text-sm font-semibold disabled:cursor-not-allowed"
        style={n === value ? { background: "var(--p)", color: "#fff", borderColor: "var(--p)" } : st}>{n === 6 ? "6+" : n}</button>
    ))}</div>
  );
}

export function DemoReservation({ s, official, slug }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  if (!s.booking) return null;
  const st = { borderColor: "var(--line)", background: "var(--bg)", color: "var(--ink)" };
  const off = !official;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (off) return;
    setError("");
    if (!/^[\d\s+()-]{7,20}$/.test(f.phone.trim())) return setError("Indique um telefone válido.");
    setBusy(true);
    try { await axios.post(`${API}/public/site/${slug}/reservations`, { ...f, people: Number(f.people) }); setDone(true); setF(EMPTY); }
    catch (err) { setError(apiError(err, "Não foi possível enviar a reserva.")); }
    setBusy(false);
  };
  return (
    <section id="reservar" className="scroll-mt-10 px-6 py-14" data-testid="demo-reservation">
      <Title kicker="Reservas">Reservar mesa</Title>
      {done ? (
        <div className="dz-card flex flex-col items-center gap-2 p-8 text-center" data-testid="reservation-success">
          <CheckCircle size={44} weight="fill" style={{ color: "var(--p)" }} />
          <h3 className="text-xl font-semibold">Pedido enviado!</h3>
          <p className="text-sm" style={{ color: "var(--muted)" }}>Vamos confirmar a sua reserva em breve por telefone ou WhatsApp.</p>
          <button onClick={() => setDone(false)} className="mt-1 text-sm font-semibold underline" style={{ color: "var(--p)" }} data-testid="reservation-new-btn">Fazer outra reserva</button>
        </div>
      ) : (
      <form onSubmit={submit} className="dz-card grid grid-cols-2 gap-3 p-5 shadow-sm" data-testid={off ? "demo-reservation-form" : "reservation-form"}>
        <label className="min-w-0 space-y-1.5 text-sm font-medium">Data<input type="date" required min={today()} disabled={off} value={f.date} onChange={set("date")} className={field} style={st} data-testid="demo-res-date" /></label>
        <label className="min-w-0 space-y-1.5 text-sm font-medium">Hora<input type="time" required disabled={off} value={f.time} onChange={set("time")} className={field} style={st} data-testid="demo-res-time" /></label>
        <div className="col-span-2 space-y-1.5 text-sm font-medium">Pessoas<People value={f.people} onChange={(n) => setF({ ...f, people: n })} st={st} disabled={off} /></div>
        {official && <>
          <label className="col-span-2 space-y-1.5 text-sm font-medium">Nome<input required minLength={2} maxLength={80} value={f.name} onChange={set("name")} autoComplete="name" className={field} style={st} data-testid="reservation-name" /></label>
          <label className="col-span-2 space-y-1.5 text-sm font-medium">Telefone / WhatsApp<input type="tel" required maxLength={20} value={f.phone} onChange={set("phone")} placeholder="+238 ..." autoComplete="tel" className={field} style={st} data-testid="reservation-phone" /></label>
          <label className="col-span-2 space-y-1.5 text-sm font-medium">Pedido especial (opcional)<textarea rows={2} maxLength={500} value={f.special_request} onChange={set("special_request")} className={field} style={st} data-testid="reservation-special" /></label>
          <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
        </>}
        {error && <p className="col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reservation-error">{error}</p>}
        <button type="submit" disabled={off || busy} className="dz-btn col-span-2 mt-1 flex h-14 items-center justify-center gap-2 text-base font-semibold text-white opacity-95 disabled:cursor-not-allowed" style={{ background: "var(--p)" }} data-testid={off ? "demo-res-submit" : "reservation-submit"}>
          {busy ? <CircleNotch size={20} className="animate-spin" /> : <><CalendarPlus size={20} weight="bold" /> Pedir reserva</>}
        </button>
        {off && <p className="col-span-2 flex items-center justify-center gap-1.5 text-xs" style={{ color: "var(--muted)" }} data-testid="demo-res-note"><LockSimple size={12} /> Ativo quando o site for oficial</p>}
      </form>
      )}
    </section>
  );
}

export function DemoInfo({ s }) {
  if (!s.address && !s.hours && !s.phone) return null;
  return (
    <section className="px-6 py-14" data-testid="demo-info">
      <Title kicker="Visite-nos">Onde estamos</Title>
      <div className="space-y-4 text-[15px]">
        {s.address && <p className="flex gap-3" data-testid="demo-address"><MapPin size={22} className="shrink-0" style={{ color: "var(--p)" }} />{s.address}</p>}
        {s.hours && <p className="flex gap-3 whitespace-pre-line" data-testid="demo-hours"><Clock size={22} className="shrink-0" style={{ color: "var(--p)" }} />{s.hours}</p>}
        {s.phone && <a href={`tel:${s.phone}`} className="flex gap-3 font-semibold" data-testid="demo-phone"><Phone size={22} className="shrink-0" style={{ color: "var(--p)" }} />{s.phone}</a>}
      </div>
      {s.address && (
        <iframe title="Mapa" loading="lazy" className="mt-6 h-60 w-full border-0" style={{ borderRadius: "var(--r)" }}
          src={`https://maps.google.com/maps?q=${encodeURIComponent(`${s.name}, ${s.address}`)}&output=embed`} />
      )}
      {s.maps_url && <a href={s.maps_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold underline" style={{ color: "var(--p)" }} data-testid="demo-maps-link">Abrir no Google Maps</a>}
    </section>
  );
}
